import { type DragEvent, useRef, useState } from 'react'
import { faCloudArrowUp } from '@fortawesome/free-solid-svg-icons'

import type { MediaAsset } from '../../shared/api'
import { Icon } from '../../shared/components'
import { toast } from '../../shared/hooks'
import { currentTranslation, fmt, interpolate, useT } from '../../shared/i18n'
import { formatBytes } from '../../shared/utils'

import { useUploadAsset } from './hooks'

export function UploadZone({
  onUploaded,
  accept,
}: {
  onUploaded?: (asset: MediaAsset) => void
  accept?: string
}) {
  const { t } = useT()
  const input = useRef<HTMLInputElement>(null)
  const uploadAsset = useUploadAsset()
  const [active, setActive] = useState(false)

  const [progress, setProgress] = useState<{
    name: string
    fraction: number
  } | null>(null)

  const handle = async (files: FileList | null) => {
    for (const file of Array.from(files ?? [])) {
      setProgress({ name: file.name, fraction: 0 })

      try {
        const asset = await uploadAsset.mutateAsync({
          file,
          onProgress: (fraction) => setProgress({ name: file.name, fraction }),
        })

        toast.success(
          fmt(currentTranslation().t.media.uploadedX, { name: file.name }),
        )
        onUploaded?.(asset)
      } catch (err) {
        toast.error(err)
      }
    }

    setProgress(null)
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setActive(false)
    void handle(e.dataTransfer.files)
  }

  return (
    <div>
      <div
        className="dropzone"
        data-active={active}
        role="button"
        tabIndex={0}
        onClick={() => input.current?.click()}
        onKeyDown={(e) =>
          (e.key === 'Enter' || e.key === ' ') && input.current?.click()
        }
        onDragOver={(e) => {
          e.preventDefault()
          setActive(true)
        }}
        onDragLeave={() => setActive(false)}
        onDrop={onDrop}
      >
        <Icon icon={faCloudArrowUp} size="2x" />
        <p>
          {interpolate(t.media.drop, {
            strong: <strong>{t.media.dropStrong}</strong>,
          })}
        </p>
        <p className="small">{t.media.dropHelp}</p>
        <input
          ref={input}
          type="file"
          hidden
          multiple
          accept={accept}
          onChange={(e) => void handle(e.target.files)}
        />
      </div>
      {progress && (
        <div style={{ marginTop: 8 }}>
          <div className="row row--between small">
            <span>{progress.name}</span>
            <span className="muted">
              {Math.round(progress.fraction * 100)}%
            </span>
          </div>
          <div className="progress" aria-hidden>
            <div
              className="progress__bar"
              style={{ width: `${progress.fraction * 100}%` }}
            />
          </div>
        </div>
      )}
      {uploadAsset.isPending && !progress && (
        <p className="small muted">Uploading… {formatBytes(0)}</p>
      )}
    </div>
  )
}
